import { useMemo, useState } from "react"
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { ChannelIcon } from "@/lib/channel-icons"
import { useFormatDateTime } from "@/lib/datetime"
import { CalendarPieceCard } from "./CalendarPieceCard"
import { CalendarBlockChip, blockOverlapsDay, blockVisible, instantBlocked } from "./CalendarBlockChip"
import type { CalendarSlot, CalendarChannel, CalendarHoliday, CalendarTemplateSlot, CalendarBlock } from "./types"

type Props = {
  anchor: string
  slots: CalendarSlot[]
  channels: CalendarChannel[]
  holidays: CalendarHoliday[]
  templateSlots: CalendarTemplateSlot[]
  blocks: CalendarBlock[]
  visibleChannels: Set<string>
}

type PieceEntry = { kind: "publish" | "upload"; slot: CalendarSlot; t: number }
type EmptyEntry = { kind: "empty"; template: CalendarTemplateSlot; t: number }
type BlockEntry = { kind: "block"; block: CalendarBlock; t: number }
type Entry = PieceEntry | EmptyEntry | BlockEntry

type DayRow = {
  iso: string
  day: Date
  isToday: boolean
  holiday: CalendarHoliday | undefined
  entries: Entry[]
  postCount: number
  slotCount: number
}

// The list view is a forward-looking agenda: today at the top, then each
// following day down to the furthest day that holds a scheduled piece. Each
// day is collapsible — days with at least one piece start expanded; days with
// none start collapsed, showing just their post/slot counts. "Expand all" and
// "Collapse all" flip every day at once. Cards and open-slot rows reuse the
// same designs as the grid views.
export function CalendarListView({
  anchor,
  slots,
  channels,
  holidays,
  templateSlots,
  blocks,
  visibleChannels
}: Props) {
  const fmt = useFormatDateTime()

  const channelBySlug = useMemo(() => {
    const map = new Map<string, CalendarChannel>()
    channels.forEach((c) => map.set(c.slug, c))
    return map
  }, [channels])

  // The feed always starts on `anchor` (today, per the controller).
  const today = useMemo(() => new Date(`${anchor}T00:00:00`), [anchor])

  // Captured once per render so today's already-passed open slots drop off.
  const now = new Date()

  const visibleSlots = useMemo(
    () => slots.filter((s) => visibleChannels.has(s.channel)),
    [slots, visibleChannels]
  )

  // Furthest local day that holds a visible scheduled piece. The feed runs
  // from today through this day (inclusive); empty trailing days never show.
  const lastDay = useMemo(() => {
    let max = today
    for (const s of visibleSlots) {
      const d = startOfLocalDay(new Date(s.publish_at))
      if (d.getTime() > max.getTime()) max = d
    }
    return max
  }, [visibleSlots, today])

  // A slot is "taken" — and so hides any template at the same instant — keyed
  // by channel + exact publish moment (matching the backend open-slot rule).
  const takenKeys = useMemo(() => {
    const set = new Set<string>()
    for (const s of visibleSlots) set.add(slotKey(s.channel, s.publish_at))
    return set
  }, [visibleSlots])

  const days = useMemo(() => {
    const out: Date[] = []
    const d = new Date(today)
    while (d.getTime() <= lastDay.getTime()) {
      out.push(new Date(d))
      d.setDate(d.getDate() + 1)
    }
    return out
  }, [today, lastDay])

  // The day rows we actually render: today (always) plus any later day that
  // holds at least one piece or open slot. Computed inline (not memoized) so
  // `now`'s drift over a session keeps the open-slot cutoff fresh.
  const rows: DayRow[] = days.flatMap((day, idx) => {
    const isToday = idx === 0
    const iso = localIso(day)

    const pieceEntries: PieceEntry[] = visibleSlots.flatMap((s) => {
      const out: PieceEntry[] = []
      const pub = new Date(s.publish_at)
      if (sameLocalDay(pub, day)) {
        out.push({ kind: "publish", slot: s, t: pub.getTime() })
      }
      if (s.use_platform_scheduling && s.upload_at) {
        const up = new Date(s.upload_at)
        if (sameLocalDay(up, day)) {
          out.push({ kind: "upload", slot: s, t: up.getTime() })
        }
      }
      return out
    })

    // Open template slots: visible channel, not already taken, not inside a
    // calendar block, still in the future (so today's past slots drop off).
    const seenEmpty = new Set<string>()
    const emptyEntries: EmptyEntry[] = templateSlots.flatMap((t) => {
      if (!visibleChannels.has(t.channel)) return []
      if (instantBlocked(blocks, t.channel, t.publish_at)) return []
      const at = new Date(t.publish_at)
      if (!sameLocalDay(at, day)) return []
      const key = slotKey(t.channel, t.publish_at)
      if (takenKeys.has(key)) return []
      if (at.getTime() < now.getTime()) return []
      if (seenEmpty.has(key)) return []
      seenEmpty.add(key)
      return [{ kind: "empty", template: t, t: at.getTime() }]
    })

    // Operator-placed blocks overlapping this day, sorted at their start.
    const blockEntries: BlockEntry[] = blocks.flatMap((b) => {
      if (!blockVisible(b, visibleChannels)) return []
      if (!blockOverlapsDay(b, day)) return []
      return [{ kind: "block", block: b, t: new Date(b.starts_at).getTime() }]
    })

    // Skip empty future days entirely; always render today as the anchor.
    if (!isToday && pieceEntries.length === 0 && emptyEntries.length === 0 && blockEntries.length === 0) return []

    const entries: Entry[] = [...pieceEntries, ...emptyEntries, ...blockEntries].sort((a, b) => a.t - b.t)
    // A piece can show as both an upload ghost and a publish card on the same
    // day — count it once.
    const postCount = new Set(pieceEntries.map((e) => e.slot.id)).size

    return [{
      iso,
      day,
      isToday,
      holiday: holidays.find((h) => h.date === iso),
      entries,
      postCount,
      slotCount: emptyEntries.length,
    }]
  })

  // Per-day collapse state. Default: collapsed when the day has no pieces.
  // An explicit entry here (set by toggling a day or expand/collapse-all)
  // overrides that default.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})
  const isCollapsed = (row: DayRow) =>
    row.iso in overrides ? overrides[row.iso] : row.postCount === 0
  const toggleDay = (row: DayRow) =>
    setOverrides((prev) => ({ ...prev, [row.iso]: !isCollapsed(row) }))
  const setAll = (collapsed: boolean) =>
    setOverrides(Object.fromEntries(rows.map((r) => [r.iso, collapsed])))

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-1 flex items-center justify-end gap-4 text-xs">
        <button
          type="button"
          onClick={() => setAll(false)}
          className="inline-flex items-center gap-1 text-ink-muted hover:text-ink-display"
        >
          <ChevronsUpDown className="h-3 w-3" /> Expand all
        </button>
        <button
          type="button"
          onClick={() => setAll(true)}
          className="inline-flex items-center gap-1 text-ink-muted hover:text-ink-display"
        >
          <ChevronsDownUp className="h-3 w-3" /> Collapse all
        </button>
      </div>

      {rows.map((row, idx) => {
        const collapsed = isCollapsed(row)
        const Chevron = collapsed ? ChevronRight : ChevronDown
        return (
          <section key={row.iso} className={cn("py-3", idx > 0 && "border-t border-hairline")}>
            <button
              type="button"
              onClick={() => toggleDay(row)}
              aria-expanded={!collapsed}
              className="flex w-full items-center gap-2 text-left"
            >
              <Chevron className="h-3.5 w-3.5 shrink-0 text-ink-muted" aria-hidden />
              <span className={cn("text-sm font-semibold", row.isToday ? "text-ink-display" : "text-ink-body")}>
                {row.day.toLocaleDateString(undefined, { weekday: "long" })}
              </span>
              <span className="text-xs text-ink-muted">
                {row.day.toLocaleDateString(undefined, { month: "long", day: "numeric" })}
              </span>
              {row.isToday && (
                <span className="rounded bg-accent-faded px-1.5 py-0.5 text-[10px] font-medium text-ink-display">
                  Today
                </span>
              )}
              {row.holiday && (
                <span className="truncate text-[10px] text-ink-muted" title={row.holiday.name}>
                  · {row.holiday.name}
                </span>
              )}
              {collapsed && (
                <span className="ml-auto shrink-0 text-[11px] text-ink-muted">
                  {countLabel(row.postCount, row.slotCount)}
                </span>
              )}
            </button>

            {!collapsed && (
              <div className="mt-2 flex flex-col gap-1.5 pl-5">
                {row.entries.length === 0 ? (
                  <p className="text-xs text-ink-muted">Nothing scheduled.</p>
                ) : (
                  row.entries.map((entry) => {
                    if (entry.kind === "block") {
                      return (
                        <CalendarBlockChip
                          key={`block-${entry.block.id}`}
                          block={entry.block}
                          channels={channels}
                          className="px-2 py-1 text-[11px]"
                        />
                      )
                    }
                    if (entry.kind === "empty") {
                      const ch = channelBySlug.get(entry.template.channel)
                      if (!ch) return null
                      return (
                        <div
                          key={`empty-${entry.template.channel}-${entry.t}`}
                          className="flex items-center gap-2 rounded border border-dashed border-hairline px-2 py-1 text-[11px] text-ink-muted"
                          title={`Open ${ch.name} slot`}
                        >
                          <ChannelIcon
                            channelType={ch.channel_type}
                            color={ch.color}
                            className="h-3 w-3 shrink-0 opacity-60"
                          />
                          <span className="font-medium">{ch.name}</span>
                          <span aria-hidden>·</span>
                          <span className="whitespace-nowrap">{fmt(entry.template.publish_at, { date: false })}</span>
                          <span className="ml-auto text-[9px] uppercase tracking-wide">Open slot</span>
                        </div>
                      )
                    }

                    const ch = channelBySlug.get(entry.slot.channel)
                    if (!ch) return null
                    return (
                      <CalendarPieceCard
                        key={entry.kind === "upload" ? `upload-${entry.slot.id}` : entry.slot.id}
                        slot={entry.slot}
                        channel={ch}
                        variant={entry.kind}
                        layout="list"
                      />
                    )
                  })
                )}
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function countLabel(posts: number, slots: number) {
  if (posts === 0 && slots === 0) return "Nothing scheduled"
  const p = `${posts} ${posts === 1 ? "post" : "posts"}`
  const s = `${slots} ${slots === 1 ? "slot" : "slots"}`
  return `${p} · ${s}`
}

function slotKey(channel: string, iso: string) {
  return `${channel}|${new Date(iso).getTime()}`
}

function startOfLocalDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function localIso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function sameLocalDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
